import { mount } from 'base/ui/overlay';
import classNames from 'classnames';
import React, { useRef } from 'react';
import ReactFocusLock from 'react-focus-lock';
import { Button } from './button';
import styles from './dialog.css';
import { IconClear } from './icons';
import { Layer } from './Layer';
import { Rows } from './layout';
import { Scrollable } from './scrollable';
import { Title5 } from './typography';

type DialogProps = {
  title?: string;
  children: React.ReactNode;
  buttons?: React.ReactNode;
  onClose?(): void;
  fixedWidth?: boolean;
  width?: 'narrow' | 'regular' | 'medium' | 'wide' | 'fullscreen';
  height?: 'regular' | 'bleed';
  disableFocusLock?: boolean;
  controls?: React.ReactNode;
};

export function Dialog({
  title,
  children,
  buttons,
  onClose,
  fixedWidth,
  width = 'regular',
  height = 'regular',
  disableFocusLock,
  controls,
}: DialogProps) {
  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      // Stop webflow moving the selection while people interact with the dialog
      //   Why do we do this? This component isn't used inside webflow.
      e.stopPropagation();
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    },
    [onClose],
  );

  const showHeader = title != null;
  return (
    <DialogLayer onClose={onClose} disableFocusLock={disableFocusLock}>
      <div
        className={classNames(
          styles.dialog,
          styles[width],
          styles[`height-${height}`],
          fixedWidth && styles.fixedWidth,
        )}
        onKeyDown={onKeyDown}
        tabIndex={0}
        onClick={stopPropagation}
      >
        {showHeader && (
          <div className={styles.header}>
            <Title5>{title}</Title5>
            {onClose && (
              <Button icon={IconClear} variant="tertiary" onClick={onClose} />
            )}
          </div>
        )}
        <Scrollable
          overflowStyle={height === 'bleed' ? 'none' : undefined}
          hideScrollBar={height === 'bleed'}
        >
          {!showHeader && <div className={styles.dialogPaddingTop} />}
          <div className={styles.contentWrapper}>
            <Rows spacing={16}>{children}</Rows>
          </div>
          {buttons && <div className={styles.buttons}>{buttons}</div>}
          {height !== 'bleed' && <div className={styles.dialogPaddingBottom} />}
        </Scrollable>
        {controls && <div className={styles.controls}>{controls}</div>}
      </div>
    </DialogLayer>
  );
}

export function openDialog(props: DialogProps) {
  return mount(<Dialog {...props} />);
}

function stopPropagation(e: React.MouseEvent) {
  e.stopPropagation();
}

export const DisableDialogLayer_PlaygroundOnly =
  React.createContext<boolean>(false);

export function DialogLayer({
  children,
  onClose,
  disableFocusLock,
}: {
  children: React.ReactNode;
  onClose?: () => void;
  disableFocusLock?: boolean;
}) {
  const disableLayer = React.useContext(DisableDialogLayer_PlaygroundOnly);
  const ref = useRef<HTMLDivElement | null>(null);

  if (disableLayer) return <>{children}</>;

  const handleOutsideClick = onClose
    ? (e: React.MouseEvent<HTMLElement>) => {
        if (e.target !== ref.current) {
          return;
        }

        onClose();
      }
    : undefined;

  const handleKeyDown = onClose
    ? (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key !== 'Escape') {
          return;
        }

        e.preventDefault();
        onClose();
      }
    : undefined;

  return (
    <Layer onOutsideClick={onClose}>
      <div
        ref={ref}
        className={styles.container}
        onClick={handleOutsideClick}
        onKeyDown={handleKeyDown}
      >
        <ReactFocusLock
          disabled={disableFocusLock}
          persistentFocus={true}
          returnFocus={true}
        >
          {children}
        </ReactFocusLock>
      </div>
    </Layer>
  );
}

export function DialogHeroImage({
  src,
  alt,
  aspectRatio,
}: {
  src: string;
  alt: string;
  aspectRatio: string;
}) {
  return (
    <img
      className={styles.heroImage}
      src={src}
      alt={alt}
      style={{ aspectRatio }}
    />
  );
}
